import BaseModal from "./BaseModal";
import { TextInputContainer } from "../forms/TextInput";
import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { firebaseAuth } from "../../firebase";
import { useSnackbar } from "notistack";
import {
    EmailAuthProvider,
    reauthenticateWithCredential,
    updateEmail,
    updatePassword,
    multiFactor,
    TotpMultiFactorGenerator,
} from "firebase/auth";
import { put, USERS } from "../../services/Client";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";

import { QRCodeSVG } from "qrcode.react";

export default function ChangeCredentialsModal({ open, onClose }) {

    const { t } = useTranslation();

    // --- Stato credenziali ---
    const [newEmail, setNewEmail] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [repeatPassword, setRepeatPassword] = useState("");
    const [error, setError] = useState({});
    const [showPasswords, setShowPasswords] = useState(false);

    // --- Stato 2FA TOTP ---
    const [mfaEnrolled, setMfaEnrolled] = useState(false);
    const [mfaOtp, setMfaOtp] = useState("");
    const [mfaStep, setMfaStep] = useState("idle"); // "idle" | "sending" | "verifying" | "done"
    const [totpSecret, setTotpSecret] = useState(null);
    const [totpQrUrl, setTotpQrUrl] = useState("");
    const [mfaError, setMfaError] = useState("");

    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    // Controlla se l'utente ha già il 2FA attivo quando la modale si apre
    useEffect(() => {
        if (open) {
            const user = firebaseAuth.currentUser;
            if (user) {
                const factors = multiFactor(user).enrolledFactors;
                setMfaEnrolled(factors.length > 0);
            }
        }
    }, [open]);

    // --- Avvia enrollment TOTP ---
    const startTotpEnroll = async () => {
        setMfaError("");
        if (!currentPassword) {
            setMfaError(t('insertCurrentPasswordFirst'));
            return;
        }
        try {
            setMfaStep("sending");
            const user = firebaseAuth.currentUser;
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            const multiFactorUser = multiFactor(user);
            const session = await multiFactorUser.getSession();
            const secret = await TotpMultiFactorGenerator.generateSecret(session);
            setTotpSecret(secret);
            setTotpQrUrl(secret.generateQrCodeUrl(user.email, "Pin Bike"));
            setMfaStep("verifying");
        } catch (e) {
            console.error(e);
            if (e.code === "auth/wrong-password" || e.code === "auth/invalid-credential") {
                setMfaError(t('wrongPassword'));
            } else {
                setMfaError(e.message);
            }
            setMfaStep("idle");
        }
    };

    // --- Conferma OTP TOTP e completa enrollment ---
    const confirmTotpEnroll = async () => {
        setMfaError("");
        if (!mfaOtp) {
            setMfaError(t('mandatoryField'));
            return;
        }
        try {
            const assertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, mfaOtp);
            const user = firebaseAuth.currentUser;
            await multiFactor(user).enroll(assertion, "Authenticator App");
            setMfaEnrolled(true);
            setMfaStep("done");
            setMfaOtp("");
            setTotpSecret(null);
            setTotpQrUrl("");
            enqueueSnackbar(t('twoFactorEnabled'), { variant: "success" });
        } catch (e) {
            console.error(e);
            setMfaError(t('wrongOtp'));
        }
    };

    // --- Rimuovi 2FA ---
    const unenrollMfa = async () => {
        try {
            const user = firebaseAuth.currentUser;
            const multiFactorUser = multiFactor(user);
            const factor = multiFactorUser.enrolledFactors[0];
            await multiFactorUser.unenroll(factor);
            setMfaEnrolled(false);
            setMfaStep("idle");
            enqueueSnackbar(t('twoFactorDisabled'), { variant: "success" });
        } catch (e) {
            console.error(e);
            enqueueSnackbar(e.message, { variant: "error" });
        }
    };

    // --- Salva credenziali ---
    const save = () => {
        if (currentPassword === "") {
            setError({ old: t('mandatoryField') });
            return;
        }
        if (newPassword !== repeatPassword) {
            setError({ repeat: t('passwordNotMatch'), new: t('passwordNotMatch') });
            return;
        }

        const user = firebaseAuth.currentUser;
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        const currentEmail = user.email;

        reauthenticateWithCredential(user, credential)
            .then(async () => {
                if (newEmail && newEmail !== currentEmail) {
                    try {
                        await updateEmail(user, newEmail);
                        try {
                            await put(USERS, { body: { email: newEmail }, elem: user.uid });
                            enqueueSnackbar(t('emailSaved'), { variant: "success" });
                        } catch {
                            await updateEmail(user, currentEmail);
                            enqueueSnackbar(t('errorSavingNewEmail'), { variant: "error" });
                        } finally {
                            queryClient.invalidateQueries([USERS, { user: user.uid }]);
                        }
                    } catch {
                        enqueueSnackbar(t('errorSavingNewEmail'), { variant: "error" });
                    }
                } else if (newEmail) {
                    enqueueSnackbar(t('sameCurrentAndNewEmail'), { variant: "info" });
                }

                if (newPassword) {
                    try {
                        await updatePassword(user, newPassword);
                        enqueueSnackbar(t('newPasswordSaved'), { variant: "success" });
                    } catch {
                        enqueueSnackbar(t('errorSavingNewPassword'), { variant: "error" });
                    }
                }
            })
            .catch(() => enqueueSnackbar(t('authenticationError'), { variant: "error" }));

        close();
    };

    const close = () => {
        setNewEmail("");
        setCurrentPassword("");
        setNewPassword("");
        setRepeatPassword("");
        setError({});
        setMfaStep("idle");
        setMfaOtp("");
        setMfaError("");
        setTotpSecret(null);
        setTotpQrUrl("");
        onClose();
    };

    return (
        <BaseModal
            open={open}
            onClose={close}
            onSave={save}
            iconButton={showPasswords ? <VisibilityOff /> : <Visibility />}
            onClickButton={() => setShowPasswords(!showPasswords)}
        >
            <Grid container spacing={4} style={{ margin: 0, width: "100%", marginBottom: "1.5rem" }}>

                {/* Sezione credenziali */}
                <TextInputContainer
                    xs={6}
                    label={t('newEmail')}
                    value={newEmail}
                    onTextChange={(value) => { setNewEmail(value.trim()); setError({}); }}
                    error={error.email}
                />
                <TextInputContainer
                    xs={6}
                    required
                    label={t('currentPassword')}
                    value={currentPassword}
                    type={showPasswords ? "text" : "password"}
                    onTextChange={(value) => { setCurrentPassword(value.trim()); setError({}); }}
                    error={error.old}
                />
                <TextInputContainer
                    xs={6}
                    label={t('newPassword')}
                    value={newPassword}
                    type={showPasswords ? "text" : "password"}
                    onTextChange={(value) => { setNewPassword(value.trim()); setError({}); }}
                    error={error.new}
                />
                <TextInputContainer
                    xs={6}
                    label={t('repeatPassword')}
                    value={repeatPassword}
                    type={showPasswords ? "text" : "password"}
                    onTextChange={(value) => { setRepeatPassword(value.trim()); setError({}); }}
                    error={error.repeat}
                />

                {/* Sezione 2FA TOTP */}
                <Grid item xs={12}>
                    <Divider style={{ marginBottom: "1rem" }} />
                    <Typography variant="subtitle1" style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
                        {t('twoFactorAuth')}
                    </Typography>

                    {mfaEnrolled ? (
                        // 2FA già attivo
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item>
                                <Typography variant="body2" color="textSecondary">
                                    ✅ {t('twoFactorEnabled')}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    onClick={unenrollMfa}
                                >
                                    {t('twoFactorDisable')}
                                </Button>
                            </Grid>
                        </Grid>

                    ) : mfaStep === "idle" ? (
                        // Step 0 — bottone per avviare enrollment
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={startTotpEnroll}
                                >
                                    {t('twoFactorAuth')} →
                                </Button>
                            </Grid>
                            {mfaError && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" style={{ color: "#000" }}>{mfaError}</Typography>
                                </Grid>
                            )}
                        </Grid>

                    ) : mfaStep === "sending" ? (
                        // Caricamento
                        <Grid container spacing={2} alignItems="center">
                            <Grid item><CircularProgress size={20} /></Grid>
                            <Grid item><Typography variant="body2">{t('sending...')}</Typography></Grid>
                        </Grid>

                    ) : mfaStep === "verifying" ? (
                        // Step 1 — mostra QR code e chiedi il codice
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="body2" style={{ marginBottom: "0.5rem" }}>
                                    {t('totpScanQr')}
                                </Typography>
                                {totpQrUrl && (
                                    <QRCodeSVG value={totpQrUrl} size={160} />
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary">
                                    {t('totpManualKey')}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    style={{ fontFamily: "monospace", wordBreak: "break-all", display: "block" }}
                                >
                                    {totpSecret?.secretKey}
                                </Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={t('otpCode')}
                                    value={mfaOtp}
                                    onChange={(e) => { setMfaOtp(e.target.value); setMfaError(""); }}
                                    onKeyPress={(e) => { if (e.key === 'Enter') confirmTotpEnroll(); }}
                                    error={!!mfaError}
                                    helperText={mfaError}
                                    FormHelperTextProps={{ style: { color: "#000" } }}
                                    inputProps={{ maxLength: 6 }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    onClick={confirmTotpEnroll}
                                >
                                    {t('verify')}
                                </Button>
                            </Grid>
                            <Grid item xs={12}>
                                <Button size="small" onClick={() => { setMfaStep("idle"); setMfaOtp(""); setMfaError(""); }}>
                                    ← {t('back')}
                                </Button>
                            </Grid>
                        </Grid>

                    ) : null}
                </Grid>

            </Grid>
        </BaseModal>
    );
}
